import { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Heart, Share2, MapPin, Clock, Coffee, Calendar, Eye } from "lucide-react";
import { getCuisineEmoji } from "@/utils/cuisineEmojiMap";
import { BrandLogo } from "@/components/shared/BrandLogo";
import { API, axios } from "@/api";
import { toast } from "sonner";
import SpotSubmissionSheet from "@/components/trucks/SpotSubmissionSheet";
import WaitTimeDisplay from "@/components/shared/WaitTimeDisplay";
import WaitTimeSheet from "@/components/shared/WaitTimeSheet";
import { useSSEWithFallback } from "@/hooks/useSSEWithFallback";

// Truck silhouette SVG watermark
const TruckWatermark = () => (
  <svg className="absolute bottom-2 right-2 w-20 h-12 opacity-[0.08]" viewBox="0 0 120 60" fill="white">
    <rect x="5" y="20" width="70" height="30" rx="4" />
    <rect x="60" y="10" width="40" height="40" rx="4" />
    <circle cx="25" cy="52" r="8" />
    <circle cx="80" cy="52" r="8" />
    <rect x="65" y="18" width="12" height="12" rx="2" fill="rgba(0,0,0,0.3)" />
    <rect x="82" y="18" width="12" height="12" rx="2" fill="rgba(0,0,0,0.3)" />
  </svg>
);

// ── Live Status Badge ──
function LiveBadge({ business, liveData }) {
  const session = liveData?.live_session || business?.live_session;
  const todayLoc = liveData?.today_location || business?.today_location;

  if (session?.status === "live") {
    return (
      <div className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-black/70 backdrop-blur-sm" data-testid="live-badge-live">
        <div className="w-2 h-2 rounded-full bg-[#22C55E] animate-pulse" />
        <span className="text-[10px] font-bold text-white">LIVE</span>
      </div>
    );
  }

  if (session?.status === "scheduled") {
    return (
      <div className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-black/70 backdrop-blur-sm" data-testid="live-badge-scheduled">
        <Calendar className="w-2.5 h-2.5 text-blue-400" />
        <span className="text-[10px] font-bold text-white">Scheduled</span>
      </div>
    );
  }

  if (todayLoc?.spotted_at) {
    const elapsed = Math.round((Date.now() - new Date(todayLoc.spotted_at).getTime()) / 60000);
    if (elapsed < 240) {
      return (
        <div className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-black/70 backdrop-blur-sm" data-testid="live-badge-spotted">
          <div className="w-2 h-2 rounded-full bg-[#F59E0B]" />
          <span className="text-[10px] font-bold text-white">Spotted {elapsed}m ago</span>
        </div>
      );
    }
  }

  if (!todayLoc && !session) return null;

  return (
    <div className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-black/70 backdrop-blur-sm" data-testid="live-badge-offline">
      <div className="w-2 h-2 rounded-full bg-white/30" />
    </div>
  );
}

// ── Cuisine Pills ──
function CuisinePills({ tags }) {
  if (!tags || tags.length === 0) return null;
  return (
    <div className="flex gap-1.5 overflow-x-auto no-scrollbar py-1" data-testid="cuisine-pills">
      {tags.map(tag => (
        <span key={tag} className="shrink-0 px-2 py-0.5 rounded-full bg-[#1C1C1E] text-white text-[10px] font-medium whitespace-nowrap">
          {getCuisineEmoji(tag)} {tag}
        </span>
      ))}
    </div>
  );
}

export default function FoodTruckCard({ business, isFavorited, onToggleFavorite, onShare }) {
  const navigate = useNavigate();
  const [liveData, setLiveData] = useState(null);
  const [showSpot, setShowSpot] = useState(false);
  const [showWaitSheet, setShowWaitSheet] = useState(false);
  const [weather, setWeather] = useState(null);
  const [showHourly, setShowHourly] = useState(false);

  const bid = business?.business_id;
  const hasTodayLocation = business?.today_location || liveData?.today_location;
  const hasSchedule = business?.this_week_schedule?.length > 0;
  const shouldShowBadge = hasTodayLocation || hasSchedule || business?.live_session;

  // HTTP fetch for initial load + fallback
  const fetchLive = useCallback(async () => {
    if (!bid) return;
    try {
      const res = await axios.get(`${API}/offers/${bid}/truck-live-status`);
      setLiveData(res.data);
    } catch { /* ignore */ }
  }, [bid]);

  // SSE-powered live status with fallback polling (shared connection via SSEContext)
  useSSEWithFallback(
    "truck_status_changed",
    (data) => { if (data.business_id === bid) fetchLive(); },
    fetchLive,
    (data) => data.business_id === bid,
  );

  // Fetch weather for truck location
  useEffect(() => {
    if (!bid) return;
    axios.get(`${API}/weather/business/${bid}`).then(res => {
      if (res.data?.weather) setWeather(res.data.weather);
    }).catch(() => {}).catch(() => {});
  }, [bid]);

  const [topBadge, setTopBadge] = useState(null);

  // Fetch top badge for this truck
  useEffect(() => {
    if (!bid) return;
    axios.get(`${API}/vendor/badges/${bid}`).then(res => {
      if (res.data?.top_badge) setTopBadge(res.data.top_badge);
    }).catch(() => {});
  }, [bid]);

  const photo = business?.truck_photo || business?.logo_url || business?.image_url;
  const cuisineTags = business?.cuisine_tags || [];
  const city = business?.city;
  const offerTitle = business?.offer_title || business?.offer_summary;
  const followersCount = business?.favorites_count || 0;
  const spotCount = business?.spot_count_this_month || 0;
  const hasPunch = business?.punch_card_active;
  const since = business?.in_business_since;

  return (
    <div
      className="relative rounded-2xl overflow-hidden shadow-lg cursor-pointer transition-transform hover:scale-[1.01] active:scale-[0.99]"
      style={{ background: "linear-gradient(135deg, #F59E0B 0%, #D97706 100%)" }}
      onClick={() => navigate(`/truck/${bid}`)}
      data-testid={`truck-card-${bid}`}
    >
      <TruckWatermark />

      {/* Photo / Fallback */}
      <div className="relative w-full aspect-video overflow-hidden">
        {photo ? (
          <img src={photo} alt={business?.name} className="w-full h-full object-cover" loading="lazy" />
        ) : business?.logo_url ? (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#F59E0B] to-[#B45309]">
            <BrandLogo name={business?.name} logoUrl={business?.logo_url} size="lg" cuisineTag={cuisineTags[0]} />
          </div>
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#F59E0B] to-[#B45309]">
            <span className="text-5xl">{cuisineTags.length > 0 ? getCuisineEmoji(cuisineTags[0]) : "🚚"}</span>
          </div>
        )}
        {/* Dark gradient overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />

        {/* TRUCK pill badge + vendor badge — top left */}
        <div className="absolute top-2.5 left-2.5 flex gap-1.5">
          <span className="px-2.5 py-1 rounded-full bg-[#1C1C1E] text-white text-[9px] font-black tracking-wider" data-testid="truck-badge">
            TRUCK
          </span>
          {topBadge && (
            <span className="px-2 py-1 rounded-full bg-[#F59E0B] text-black text-[9px] font-bold" data-testid="vendor-achievement-badge">
              {topBadge.label}
            </span>
          )}
        </div>

        {/* Live status badge — top right */}
        {shouldShowBadge && (
          <div className="absolute top-2.5 right-2.5">
            <LiveBadge business={business} liveData={liveData} />
          </div>
        )}

        {/* Truck name — bottom of photo */}
        <div className="absolute bottom-2 left-3 right-3">
          <h3 className="text-lg font-black text-white leading-tight truncate" data-testid="truck-name">
            {business?.name}
          </h3>
        </div>
      </div>

      {/* Card body */}
      <div className="px-3 pt-2 pb-3 relative z-10">
        {/* Weather line (Task 2) */}
        {weather && (
          <div data-testid="truck-weather-line">
            <button
              onClick={(e) => { e.stopPropagation(); setShowHourly(!showHourly); }}
              className="text-[10px] text-white/50 mb-1 hover:text-white/70 transition"
              data-testid="weather-toggle"
            >
              {weather.condition_emoji} {weather.temperature_f}°F — {weather.condition.split("_").map(w => w[0].toUpperCase() + w.slice(1)).join(" ")}
            </button>
            {showHourly && weather.hourly_forecast && (
              <div className="flex gap-2.5 overflow-x-auto no-scrollbar pb-1" data-testid="hourly-forecast-row">
                {weather.hourly_forecast.slice(0, 4).map((h, i) => (
                  <div key={i} className="flex flex-col items-center shrink-0 text-white/40">
                    <span className="text-[9px]">{h.hour > 12 ? `${h.hour - 12}PM` : h.hour === 0 ? "12AM" : h.hour === 12 ? "12PM" : `${h.hour}AM`}</span>
                    <span className="text-xs">{h.condition_emoji}</span>
                    <span className="text-[9px]">{h.temperature_f}°F</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Cuisine pills */}
        <CuisinePills tags={cuisineTags} />

        {/* Wait time */}
        <WaitTimeDisplay
          waitMinutes={business?.wait_time_reported}
          waitCategory={business?.wait_time_category}
          reportedAt={business?.wait_time_reported_at}
          reporterCount={business?.wait_time_reported_by_count}
          onTap={() => setShowWaitSheet(true)}
          compact
        />

        {/* Key info line */}
        <p className="text-[10px] text-white/60 mt-1 truncate" data-testid="truck-info-line">
          {since ? `In business since ${since}` : city ? `Serves ${city}` : ""}
          {business?.nearest_location?.distance_miles ? ` · ${business.nearest_location.distance_miles.toFixed(1)} mi` : ""}
        </p>

        {/* Birthday offer preview */}
        {offerTitle && (
          <p className="text-[11px] text-white/80 mt-1 truncate font-medium" data-testid="truck-offer-preview">
            🎂 {offerTitle}
          </p>
        )}

        {/* Compact metrics row with punch card progress dots */}
        <div className="flex items-center gap-3 mt-2 text-[9px] text-white/40" data-testid="truck-metrics-row">
          <span>{followersCount} follower{followersCount !== 1 ? "s" : ""}</span>
          <span>Spotted {spotCount}x this month</span>
          {hasPunch && (
            <span className="flex items-center gap-0.5">
              <Coffee className="w-3 h-3 text-white/50" />
            </span>
          )}
        </div>
        {/* Punch card dot progress (Task 8) */}
        {business?.punch_progress && (
          <div className="flex items-center gap-1 mt-1.5" data-testid="punch-dot-progress">
            {Array.from({ length: business.punch_progress.target || 10 }).map((_, i) => (
              <div
                key={i}
                className={`rounded-full transition-all ${
                  i < (business.punch_progress.punches || 0)
                    ? "w-2 h-2 bg-white"
                    : "w-1.5 h-1.5 bg-white/25"
                }`}
              />
            ))}
            <span className="text-[8px] text-white/40 ml-1">
              {business.punch_progress.punches}/{business.punch_progress.target}
            </span>
          </div>
        )}

        {/* Action buttons */}
        <div className="flex gap-2 mt-2.5">
          <button
            onClick={(e) => { e.stopPropagation(); onToggleFavorite?.(bid); }}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-bold transition ${
              isFavorited
                ? "bg-[#F59E0B] text-[#1C1C1E]"
                : "bg-black/20 text-white hover:bg-black/30"
            }`}
            data-testid="truck-follow-btn"
          >
            <Heart className={`w-3.5 h-3.5 ${isFavorited ? "fill-current" : ""}`} />
            {isFavorited ? "Following" : "Follow"}
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); setShowSpot(true); }}
            className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-black/20 text-white text-[10px] font-bold hover:bg-black/30 transition"
            data-testid="truck-spot-btn"
          >
            <Eye className="w-3 h-3" />
            I see this
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onShare?.(business); }}
            className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-black/20 text-white text-xs font-bold hover:bg-black/30 transition"
            data-testid="truck-share-btn"
          >
            <Share2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Spot submission sheet */}
      {showSpot && (
        <SpotSubmissionSheet
          businessId={bid}
          businessName={business?.name}
          onClose={() => setShowSpot(false)}
          onSuccess={() => fetchLive()}
        />
      )}

      {/* Wait time sheet */}
      {showWaitSheet && (
        <WaitTimeSheet
          businessId={bid}
          businessName={business?.name}
          onClose={() => setShowWaitSheet(false)}
          onSuccess={() => fetchLive()}
        />
      )}
    </div>
  );
}
